import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { Loader, Center } from '@/components/ui';
import { QuickTaskModal, TaskActivityFab, TaskActivityDrawer, TaskActivitySubscriber } from './components';
import { useTaskStore } from '@/stores/taskStore';

// Eager load dashboard for instant access
import TasksDashboard from './dashboard';

// Lazy load all other pages
const AssignedTasksPage = lazy(() => import('./assigned-tasks'));
const UpcomingTasksPage = lazy(() => import('./upcoming'));
const LoopedInTasksPage = lazy(() => import('./looped-in-tasks'));
const TaskBoardPage = lazy(() => import('./board'));
const MyTasksPage = lazy(() => import('./my-tasks'));
const TaskCalendarPage = lazy(() => import('./calendar'));
const AIPrioritizerPage = lazy(() => import('./ai-prioritizer'));
const TaskDetailPage = lazy(() => import('./view/detail'));
const CreateTaskPage = lazy(() => import('./create'));
const TaskNotificationsPage = lazy(() => import('./notifications'));

// Admin pages
const AdminAllTasksPage = lazy(() => import('./admin/all-tasks'));
const AdminProjectsPage = lazy(() => import('./admin/projects'));
const AdminDepartmentsPage = lazy(() => import('./admin/departments'));
const AdminLocationsPage = lazy(() => import('./admin/locations'));
const AdminReportsPage = lazy(() => import('./admin/reports'));
const AdminAiInsightsPage = lazy(() => import('./admin/ai-insights'));
const AdminLookupsPage = lazy(() => import('./admin/lookups'));
const AdminSettingsPage = lazy(() => import('./admin/settings'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Task Management Routes
 * Complete routing configuration for the Tasks domain
 */
export default function TaskManagementRoutes() {
  const location = useLocation();
  const adminKanbanFullWidth = useTaskStore((s) => s.adminKanbanFullWidth);
  // The user-facing /board route is always full-width. The admin grouping
  // pages (projects/departments/locations) flip `adminKanbanFullWidth` to
  // true when they enter Kanban view so columns can span the viewport; the
  // list view stays boxed per the user's Layout Preferences.
  const forceFullWidth = location.pathname.includes('/board') || adminKanbanFullWidth;

  return (
    <MainLayout domainKey="tasks" forceFullWidth={forceFullWidth}>
      {/* Quick Task Modal - available on every TaskManagement page */}
      <QuickTaskModal />

      {/* Activity drawer — comments and notes across every task the user is
          involved in, plus who else currently has Task Management open.
          The subscriber is mounted here rather than in MainLayout on purpose:
          its SignalR connection lifetime IS "the user has Task Management
          open", which is exactly what the Online tab reports. */}
      <TaskActivitySubscriber />
      <TaskActivityFab />
      <TaskActivityDrawer />

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<TasksDashboard />} />

          {/* User Pages */}
          <Route path="/assigned-tasks" element={<AssignedTasksPage />} />
          <Route path="/upcoming" element={<UpcomingTasksPage />} />
          <Route path="/looped-in-tasks" element={<LoopedInTasksPage />} />
          <Route path="/board" element={<TaskBoardPage />} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/calendar" element={<TaskCalendarPage />} />
          <Route path="/ai-prioritizer" element={<AIPrioritizerPage />} />

          {/* Task Detail View */}
          <Route path="/view/:id" element={<TaskDetailPage />} />

          {/* Create Task */}
          <Route path="/create" element={<CreateTaskPage />} />

          {/* Notifications */}
          <Route path="/notifications" element={<TaskNotificationsPage />} />

          {/* Admin Routes - Protected */}
          <Route
            path="/admin/all-tasks"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminAllTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminDepartmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminLocationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-insights/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminAiInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups/*"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminLookupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/tasks" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
